import React, { useState, useRef } from "react"
import { useGuias } from "../context/GuiasContext"
import {FileCheck, Search, Edit2, Trash2, Printer, Eye} from 'lucide-react'

interface ListarAlvarasProps {
  onEdit: (alvaraId: string) => void
}

const statusColor: Record<string, string> = {
  ativo: "bg-green-100 text-green-700",
  vencido: "bg-yellow-100 text-yellow-700",
  cancelado: "bg-red-100 text-red-600"
}

const MARCA_DAGUA = "https://static.lumi.new/e9/e94f3c255c99cf8881ab95aac3edd2b0.webp"

function formatDate(iso: string) {
  if (!iso) return "—"
  const d = new Date(iso + (iso.length === 10 ? "T12:00:00" : ""))
  return d.toLocaleDateString("pt-BR")
}

export default function ListarAlvaras({ onEdit }: ListarAlvarasProps) {
  const { alvaras, deleteAlvara, logoUrl, dadosPrefeitura } = useGuias()
  const [busca, setBusca] = useState("")
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [viewing, setViewing] = useState<string | null>(null)
  const printRef = useRef<HTMLDivElement>(null)

  const filtrados = alvaras.filter(a =>
    a.razaoSocial?.toLowerCase().includes(busca.toLowerCase()) ||
    a.nomeFantasia?.toLowerCase().includes(busca.toLowerCase()) ||
    a.cnpj?.includes(busca) ||
    a.numeroAlvara?.includes(busca)
  )

  const alvara = viewing ? alvaras.find(a => a._id === viewing) : null

  const handlePrint = (a: typeof alvara) => {
    if (!a) return
    const prefeitura = dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal de São José do Seridó"
    const secretaria = dadosPrefeitura?.secretaria || "Secretaria Municipal de Tributação"
    const endPref = dadosPrefeitura?.endereco || "Praça Severino Bezerra, s/n – Centro"

    const html = `
      <!DOCTYPE html><html>
      <head><meta charset="UTF-8"/><title>Alvará Nº ${a.numeroAlvara}</title>
      <style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Times New Roman',Times,serif;background:white;}@page{size:A4;margin:1.5cm;}@media print{img{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style>
      </head>
      <body onload="window.print();window.close();">
      <div style="width:100%;min-height:297mm;padding:10mm;position:relative;background:white;box-sizing:border-box;">
        <img src="${MARCA_DAGUA}" alt="" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:520px;height:520px;opacity:0.08;object-fit:contain;pointer-events:none;z-index:0;" />
        <div style="position:relative;z-index:1;">
          <div style="display:flex;align-items:center;justify-content:center;gap:16px;margin-bottom:16px;border-bottom:2px solid #004161;padding-bottom:12px;">
            <img src="${logoUrl}" style="height:70px;"/>
            <div style="text-align:center;">
              <div style="font-size:12px;font-weight:bold;color:#004161;text-transform:uppercase;">ESTADO DO RIO GRANDE DO NORTE</div>
              <div style="font-size:15px;font-weight:bold;color:#004161;text-transform:uppercase;margin-top:2px;">${prefeitura}</div>
              <div style="font-size:11px;color:#555;margin-top:2px;">${secretaria}</div>
              <div style="font-size:10px;color:#777;">${endPref}</div>
            </div>
            <img src="${MARCA_DAGUA}" style="height:70px;opacity:0.9;"/>
          </div>
          <div style="text-align:center;margin:14px 0;padding:8px;border:2px solid #004161;border-radius:4px;background:#f0f7ff;">
            <div style="font-size:18px;font-weight:bold;color:#004161;text-transform:uppercase;letter-spacing:2px;">ALVARÁ DE FUNCIONAMENTO</div>
            <div style="font-size:13px;color:#004161;margin-top:3px;">Nº <b>${a.numeroAlvara}</b> — Exercício: <b>${a.anoExercicio || new Date().getFullYear()}</b></div>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:10px;">
            <tr><td colspan="4" style="background:#004161;color:white;padding:5px 8px;font-weight:bold;">IDENTIFICAÇÃO DO ESTABELECIMENTO</td></tr>
            <tr><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;width:22%;">Razão Social</td><td colspan="3" style="border:1px solid #ccc;padding:4px 7px;">${a.razaoSocial}</td></tr>
            ${a.nomeFantasia ? `<tr><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Nome Fantasia</td><td colspan="3" style="border:1px solid #ccc;padding:4px 7px;">${a.nomeFantasia}</td></tr>` : ""}
            <tr><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">CNPJ</td><td style="border:1px solid #ccc;padding:4px 7px;">${a.cnpj}</td><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Tipo</td><td style="border:1px solid #ccc;padding:4px 7px;">${a.tipoEmpresa || "—"}</td></tr>
            <tr><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Ramo de Atividade</td><td colspan="3" style="border:1px solid #ccc;padding:4px 7px;">${a.ramoAtividade || "—"}</td></tr>
            <tr><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Responsável</td><td style="border:1px solid #ccc;padding:4px 7px;">${a.responsavel || "—"}</td><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">CPF</td><td style="border:1px solid #ccc;padding:4px 7px;">${a.cpfResponsavel || "—"}</td></tr>
          </table>
          <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:10px;">
            <tr><td colspan="4" style="background:#004161;color:white;padding:5px 8px;font-weight:bold;">ENDEREÇO</td></tr>
            <tr><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Logradouro</td><td style="border:1px solid #ccc;padding:4px 7px;">${a.endereco}</td><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Nº</td><td style="border:1px solid #ccc;padding:4px 7px;">${a.numero || "—"}</td></tr>
            <tr><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Bairro</td><td style="border:1px solid #ccc;padding:4px 7px;">${a.bairro || "—"}</td><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Município</td><td style="border:1px solid #ccc;padding:4px 7px;">${a.cidade || "São José do Seridó"}/${a.uf || "RN"}</td></tr>
          </table>
          <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:10px;">
            <tr><td colspan="4" style="background:#004161;color:white;padding:5px 8px;font-weight:bold;">DADOS DO ALVARÁ</td></tr>
            <tr><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Número</td><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;color:#004161;">${a.numeroAlvara}</td><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Exercício</td><td style="border:1px solid #ccc;padding:4px 7px;">${a.anoExercicio || "—"}</td></tr>
            <tr><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Emissão</td><td style="border:1px solid #ccc;padding:4px 7px;">${formatDate(a.dataEmissao || "")}</td><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Válido até</td><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;color:#007a33;">${formatDate(a.dataValidade || "")}</td></tr>
            ${a.taxaAlvara ? `<tr><td style="border:1px solid #ccc;padding:4px 7px;font-weight:bold;background:#f8f8f8;">Taxa</td><td colspan="3" style="border:1px solid #ccc;padding:4px 7px;">R$ ${a.taxaAlvara}</td></tr>` : ""}
          </table>
          ${a.observacoes ? `<div style="border:1px solid #ccc;padding:7px;font-size:11px;margin-bottom:10px;"><b>CONDICIONANTES / OBSERVAÇÕES:</b><p style="margin-top:3px;">${a.observacoes}</p></div>` : ""}
          <div style="font-size:10px;line-height:1.6;color:#555;text-align:justify;margin-bottom:16px;border-top:1px solid #ddd;padding-top:8px;">
            Este documento é válido somente para o exercício de <b>${a.anoExercicio}</b>, para a atividade e endereço acima especificados, devendo ser renovado anualmente.
          </div>
          <div style="display:flex;justify-content:space-between;margin-top:24px;font-size:11px;">
            <div style="text-align:center;width:40%;"><div style="border-top:1px solid #333;padding-top:5px;"><b>Secretário(a) de Tributação</b><br/><span style="font-size:10px;color:#777;">Prefeitura de São José do Seridó/RN</span></div></div>
            <div style="text-align:center;font-size:10px;color:#777;">São José do Seridó/RN, ${formatDate(a.dataEmissao || "")}</div>
            <div style="text-align:center;width:40%;"><div style="border-top:1px solid #333;padding-top:5px;"><b>Prefeito(a) Municipal</b><br/><span style="font-size:10px;color:#777;">Prefeitura de São José do Seridó/RN</span></div></div>
          </div>
          <div style="margin-top:16px;border-top:2px solid #004161;padding-top:7px;text-align:center;font-size:9px;color:#999;">
            Sistema EMIT — Prefeitura Municipal de São José do Seridó/RN | saojosedoserido.rn.gov.br
          </div>
        </div>
      </div>
      </body></html>
    `
    const w = window.open("", "_blank", "width=900,height=700")
    if (!w) return
    w.document.write(html)
    w.document.close()
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-[#004161] px-6 py-4 flex items-center gap-3">
        <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
          <FileCheck className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-white font-bold text-base">Alvarás Emitidos</h2>
          <p className="text-blue-200 text-xs">{alvaras.length} alvará(s) emitido(s)</p>
        </div>
      </div>

      {/* Busca */}
      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={busca} onChange={e => setBusca(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#004161]/30"
            placeholder="Buscar por empresa, CNPJ ou nº do alvará..."
          />
        </div>
      </div>

      {/* Lista */}
      <div className="divide-y divide-gray-50">
        {filtrados.length === 0 ? (
          <div className="py-16 text-center">
            <FileCheck className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Nenhum alvará encontrado</p>
          </div>
        ) : (
          filtrados.map(a => (
            <div key={a._id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-gray-800 text-sm truncate">{a.razaoSocial}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[a.statusAlvara || "ativo"]}`}>
                      {a.statusAlvara || "ativo"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1">
                    <span className="text-xs text-gray-500 font-mono">Nº {a.numeroAlvara}</span>
                    <span className="text-xs text-gray-400">Exercício: {a.anoExercicio}</span>
                    <span className="text-xs text-gray-400">Válido até: {formatDate(a.dataValidade || "")}</span>
                    {a.ramoAtividade && <span className="text-xs text-gray-400">{a.ramoAtividade}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => setViewing(a._id || null)}
                    className="p-1.5 text-gray-400 hover:text-[#004161] hover:bg-[#004161]/10 rounded-lg transition-colors" title="Visualizar">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => handlePrint(a)}
                    className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Imprimir">
                    <Printer className="w-4 h-4" />
                  </button>
                  <button onClick={() => onEdit(a._id || "")}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setConfirmDelete(a._id || null)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Excluir">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Visualizar */}
      {viewing && alvara && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-[#004161] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-white" />
                <h3 className="text-white font-bold">Alvará Nº {alvara.numeroAlvara}</h3>
              </div>
              <button onClick={() => setViewing(null)} className="text-white/70 hover:text-white">✕</button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Razão Social</p><p className="font-medium text-gray-800">{alvara.razaoSocial}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">CNPJ</p><p className="text-gray-800">{alvara.cnpj}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Ramo</p><p className="text-gray-800">{alvara.ramoAtividade || "—"}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Nº Alvará</p><p className="font-bold text-[#004161]">{alvara.numeroAlvara}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Emissão</p><p className="text-gray-800">{formatDate(alvara.dataEmissao || "")}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Validade</p><p className="text-green-700 font-semibold">{formatDate(alvara.dataValidade || "")}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Exercício</p><p className="text-gray-800">{alvara.anoExercicio}</p></div>
                <div><p className="text-xs text-gray-400 font-semibold uppercase">Taxa</p><p className="text-gray-800">{alvara.taxaAlvara ? `R$ ${alvara.taxaAlvara}` : "—"}</p></div>
                <div className="col-span-2"><p className="text-xs text-gray-400 font-semibold uppercase">Endereço</p><p className="text-gray-800">{[alvara.endereco, alvara.numero, alvara.bairro, alvara.cidade, alvara.uf].filter(Boolean).join(", ")}</p></div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => handlePrint(alvara)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-semibold">
                  <Printer className="w-4 h-4" /> Imprimir Alvará
                </button>
                <button onClick={() => setViewing(null)}
                  className="px-5 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <Trash2 className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h3 className="font-bold text-gray-800 mb-2">Excluir Alvará?</h3>
            <p className="text-gray-500 text-sm mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">Cancelar</button>
              <button onClick={async () => { await deleteAlvara(confirmDelete); setConfirmDelete(null) }}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 text-sm font-semibold">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
